import { Icon, theme as defaultTheme } from "@mindlercare/ui-web";
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { styled } from "styled-components";

const AUDIO_EXTENSIONS = [".mp3", ".wav", ".ogg", ".aac", ".m4a"];
const BAR_COUNT = 40;

const hashSeed = (str: string): number =>
  str
    .split("")
    .reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) & 0xfffffff, 0);

const buildWaveform = (seed: number): number[] => {
  const offset = seed * 0.001;
  return Array.from({ length: BAR_COUNT }, (_, i) => {
    const base = Math.sin(i * 3.0 + offset) * 4;
    const mid = Math.cos(i * 2.1 + offset * 1.3) * 3;
    const detail = Math.sin(i * 5.3 + offset * 0.7 + 1.2) * 2;
    const fine = Math.cos(i * 7.9 + offset * 1.9 + 2.7) * 1.5;
    return Math.max(7, 8 + base + mid + detail + fine);
  });
};

const formatTime = (seconds: number): string => {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
};

export const isAudioAsset = (asset: {
  url?: string;
  contentType?: string;
}): boolean => {
  const url = asset.url?.toLowerCase() ?? "";
  return (
    (asset.contentType?.startsWith("audio/") ?? false) ||
    AUDIO_EXTENSIONS.some((ext) => url.endsWith(ext))
  );
};

interface IcbtAudioPlayerProps {
  url: string;
}

export const IcbtAudioPlayer = ({ url }: IcbtAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveBarRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sliderFillRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<HTMLSpanElement>(null);
  const durationTimeRef = useRef<HTMLSpanElement>(null);
  const progressRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const waveformHeights = useMemo(() => buildWaveform(hashSeed(url)), [url]);

  const updateVisuals = useCallback((progress: number) => {
    progressRef.current = progress;
    waveBarRefs.current.forEach((bar, i) => {
      if (!bar) return;
      const active = progress > i / BAR_COUNT;
      bar.style.backgroundColor = active
        ? defaultTheme.COLORS.BLACK
        : defaultTheme.COLORS.WHITE;
      bar.style.opacity = active ? "1" : "0.4";
    });
    if (sliderFillRef.current) {
      sliderFillRef.current.style.width = `${progress * 100}%`;
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      const dur = audio.duration;
      if (currentTimeRef.current) {
        currentTimeRef.current.textContent = formatTime(audio.currentTime);
      }
      if (dur > 0) {
        updateVisuals(audio.currentTime / dur);
      }
    };
    const onDurationChange = () => {
      const dur = audio.duration;
      if (isFinite(dur) && dur > 0 && durationTimeRef.current) {
        durationTimeRef.current.textContent = formatTime(dur);
      }
    };
    const onEnded = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [updateVisuals]);

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      if (audio.duration > 0 && audio.currentTime >= audio.duration - 0.1) {
        audio.currentTime = 0;
      }
      audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const handleSeek = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !isFinite(audio.duration) || audio.duration <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      audio.currentTime = ratio * audio.duration;
      updateVisuals(ratio);
    },
    [updateVisuals],
  );

  return (
    <Container>
      <audio ref={audioRef} src={url} preload="metadata" />
      <WaveformRow onClick={handleSeek}>
        {waveformHeights.map((height, i) => (
          <WaveBar
            key={i}
            ref={(el) => {
              waveBarRefs.current[i] = el;
            }}
            $height={height}
          />
        ))}
      </WaveformRow>
      <SliderTrack onClick={handleSeek}>
        <SliderFill ref={sliderFillRef} />
      </SliderTrack>
      <ControlRow>
        <PlayPauseButton onClick={handlePlayPause}>
          {playing ? <Icon.Pause /> : <Icon.Play />}
        </PlayPauseButton>
        <TimeText ref={currentTimeRef}>0:00</TimeText>
        <Spacer />
        <TimeText ref={durationTimeRef}>--:--</TimeText>
      </ControlRow>
    </Container>
  );
};

const Container = styled.div`
  border-radius: ${defaultTheme.BORDER_RADIUS.LG};
  overflow: hidden;
  padding: ${defaultTheme.SPACING.MD};
  margin: ${defaultTheme.SPACING.SM} 0;
  background: linear-gradient(
    to right,
    ${defaultTheme.COLORS.PRIMARY},
    ${defaultTheme.COLORS.BRAND_ORANGE}
  );
`;

const WaveformRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 28px;
  cursor: pointer;
`;

const WaveBar = styled.div<{ $height: number }>`
  width: 2px;
  height: ${({ $height }) => $height}px;
  border-radius: 1px;
  background-color: ${defaultTheme.COLORS.WHITE};
  opacity: 0.4;
  transition: background-color 0.1s;
`;

const SliderTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.4);
  margin-top: ${defaultTheme.SPACING.SM};
  cursor: pointer;
  position: relative;
`;

const SliderFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background-color: ${defaultTheme.COLORS.BLACK};
  transition: width 0.1s linear;
`;

const ControlRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${defaultTheme.SPACING.SM};
  margin-top: ${defaultTheme.SPACING.SM};
`;

const PlayPauseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${defaultTheme.COLORS.BLACK};
  color: ${defaultTheme.COLORS.WHITE};
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.85;
  }
`;

const TimeText = styled.span`
  color: ${defaultTheme.COLORS.BLACK};
  font-size: ${defaultTheme.FONT.SIZE.SMALL};
  font-family: ${defaultTheme.FONT.FAMILY};
  user-select: none;
`;

const Spacer = styled.div`
  flex: 1;
`;
